import { ReactNode, Ref } from "react"

import { cn } from "@/lib/utils"
import { IconAlertTriangleFilled, IconInfoCircle } from "@tabler/icons-react"

import { Tooltip } from "./Tooltip"

export interface FormFieldProps {
  label?: ReactNode
  id?: string
  helperText?: ReactNode
  errorText?: string
  required?: boolean
  disabled?: boolean
  children: ReactNode
  className?: string
  length?: number
  maxLength?: number
  withCharCounter?: boolean
  hideOptionalLabel?: boolean
  vertical?: number | boolean
  labelClassName?: string
  tooltip?: string
  ref?: Ref<HTMLDivElement>
}

export const FormField = ({
  label,
  id,
  helperText,
  errorText,
  required,
  disabled,
  children,
  className,
  length,
  maxLength,
  withCharCounter,
  hideOptionalLabel,
  vertical,
  labelClassName,
  tooltip,
  ref,
}: FormFieldProps) => {
  return (
    <div
      ref={ref}
      className={cn(
        "flex flex-col gap-1",
        { "flex-row items-start gap-4": vertical },
        className,
      )}
    >
      {label || helperText || (!vertical && !required && !hideOptionalLabel) ? (
        <div
          className={cn("flex flex-col justify-center", {
            hidden: !label && !(helperText || !required),
            "min-h-10": vertical,
          })}
          style={vertical ? { width: vertical === true ? 120 : vertical } : undefined}
        >
          {label && (
            <label
              htmlFor={id}
              className={cn(
                "gap-2 text-body2 text-colors-text-secondary",
                "flex items-center",
                {
                  "text-colors-text-disabled": disabled,
                },
                labelClassName,
              )}
            >
              {label} {vertical && required ? "*" : null}
              {tooltip && (
                <Tooltip
                  content={tooltip}
                  className="max-w-sm"
                  dark
                  disableHoverableContent
                >
                  <IconInfoCircle size={14} className="shrink-0" />
                </Tooltip>
              )}
            </label>
          )}
          {(!vertical && !required && !hideOptionalLabel) || helperText ? (
            <span
              className={cn(
                "text-caption text-colors-text-tertiary",
                {
                  "text-colors-text-disabled": disabled,
                },
                labelClassName,
              )}
            >
              {!required && !hideOptionalLabel && !vertical ? "(Optional) " : ""}
              {helperText}
            </span>
          ) : null}
        </div>
      ) : null}
      <div className={cn("flex min-w-0 grow basis-0 flex-col gap-2")}>
        {children}
        {errorText || withCharCounter ? (
          <div className="flex justify-between">
            <span
              className={cn(
                "flex items-center gap-1 text-body2 text-colors-text-error",
                labelClassName,
              )}
            >
              {errorText ? (
                <>
                  <IconAlertTriangleFilled /> {errorText}
                </>
              ) : null}
            </span>
            {withCharCounter && maxLength ? (
              <FormField.Counter length={length} maxLength={maxLength} />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}

FormField.Counter = function FormFieldCounter({
  length = 0,
  maxLength,
}: {
  length?: number
  maxLength: number
}) {
  const lengthExceeded = length > maxLength

  return (
    <span
      className={cn("text-body2 text-colors-text-tertiary", {
        "text-colors-text-error": lengthExceeded,
      })}
    >
      {length}/{maxLength}
    </span>
  )
}
